"use client";

import * as React from "react";
import Link from "next/link";
import logo from "@/public/ventura-logo.png";
// import logo from "@/public/ventura-logo-cinco-de-mayo-2.png";
import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import Image from "next/image";
import { useRouter } from "next/router";
import BookButton from "../ui/BookButton";
import HeightOffset from "../navbar/HeightOffset";
import { useState } from "react";
import clsx from "clsx";
import { useEffect } from "react";
import MobileSwitcher from "../navbar/MobileSwitcher";
import MobileMenu from "../navbar/MobileMenu";

const navigation = [
  {
    name: "Services",
    links: [
      {
        name: "Web Design",
        href: "web-design",
      },
      {
        name: "VIP Support",
        href: "vip-support",
      },
      {
        name: "Email & SMS Marketing",
        href: "email-marketing",
      },
      {
        name: "Paid Search",
        href: "paid-search",
      },
      {
        name: "Social Media Marketing",
        href: "social-media-marketing",
      },
      {
        name: "Search Engine Optimization",
        href: "seo",
      },
      {
        name: "Local Listing Management",
        href: "/local-listing-management",
      },
      {
        name: "AI Customer Service Agent",
        href: "/addons/ai-customer-service-agent",
      },
    ],
  },
  {
    name: "Our Work",
    href: "our-work",
  },
  {
    name: "About Us",
    href: "about",
  },
  {
    name: "Partners",
    href: "partners",
  },
  {
    name: "Learning",
    href: "learn",
  },
];

export default function Nav() {
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div
        className={clsx(
          "fixed top-0 z-30 flex h-[81px] w-full items-center justify-between border-b bg-white p-4",
          scrolled && "border-black/10",
        )}
      >
        <div className="shrink-0 lg:w-[200px]">
          <Link
            href="/"
            className="relative z-50 select-none"
            aria-label="Home"
          >
            <div className="w-[200px]">
              <Image
                src={logo}
                alt="Ventura Web Design and Marketing"
                quality={100}
                className={
                  router.pathname === "/shopify"
                    ? "brightness-150 -hue-rotate-[140deg] saturate-50"
                    : ""
                }
                sizes="200px"
              />
            </div>
          </Link>
        </div>

        <div className="hidden w-full select-none justify-center xl:flex">
          <NavigationMenu>
            <NavigationMenuList>
              {navigation.map((item) =>
                item.links ? (
                  <NavigationMenuItem key={item.name}>
                    <NavigationMenuTrigger>{item.name}</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid gap-1 md:w-[400px] lg:w-[600px] lg:grid-cols-[.75fr_1fr]">
                        {item.links.map((link) => (
                          <ListItem
                            key={link.name}
                            title={link.name}
                            href={`/${link.href}`}
                            className={
                              router.pathname !== "/" &&
                              router.pathname.includes(link.href)
                                ? "select-none bg-accent/50"
                                : "select-none"
                            }
                          />
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ) : (
                  <NavigationMenuItem key={item.name}>
                    <Link
                      prefetch={false}
                      href={`/${item.href}`}
                      legacyBehavior
                      passHref
                    >
                      <NavigationMenuLink
                        className={`${router.pathname !== "/" && router.pathname.includes(item.href) ? "select-none !bg-accent/50" : "select-none"} ${navigationMenuTriggerStyle()}`}
                      >
                        {item.name}
                      </NavigationMenuLink>
                    </Link>
                  </NavigationMenuItem>
                ),
              )}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="hidden shrink-0 justify-end lg:w-[200px] xl:flex">
          <BookButton
            color={clsx(router.pathname === "/shopify" ? "green" : "blue")}
            text="Contact Us"
            link="/contact-us"
            custom="!w-[155px]"
          />
        </div>

        <MobileSwitcher mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      </div>

      <MobileMenu mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <HeightOffset />
    </>
  );
}

const ListItem = ({ className, title, children, ...props }) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          className={cn(
            "block select-none rounded-lg p-4 leading-none no-underline outline-none transition-colors hover:bg-accent/50 hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className,
          )}
          {...props}
        >
          <div className="font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
};
